/**
 * Voice Recorder Component
 * Recording overlay for voice messages with Azure Speech integration
 */

import React, { useState, useEffect, useRef } from "react";
import { TouchableOpacity, Platform, Animated } from "react-native";
import { useTranslation } from "react-i18next";
import { Box, VStack, HStack, Text } from "@gluestack-ui/themed";
import Ionicons from '../ui/Ionicons';
import { colors } from "../../theme";
import { ConversationSpeech } from "../../modules/ConversationSpeech";
import type { ConversationResult, EventSubscription } from "../../modules/ConversationSpeech";
import type { PronunciationResult } from "../../types/conversation";
import { selectProfile } from "../../modules/assessmentProfiles";
import { getSpeechRelayConnection } from "../../services/speechRelayAuth";
import { nativeErrorCode } from "../../modules/nativeErrorCode";
import { useConversationStore } from "../../store/conversationStore";
import { useUserPreferencesStore } from "../../store/userPreferencesStore";
import { selectDisplayName, selectTargetSttLocale, selectTargetPronunciationFeatures } from "../../store/userPreferencesSelectors";
import { useSocketStatus } from "../../services/socket";
import { createLogger } from "../../utils/logger";
import { useDisplayTheme } from "../../hooks/useDisplayTheme";
import { E2E_MODE } from "../../config/constants";

const log = createLogger("VoiceRecorder");

/** Build phrase list with app name and optional user name */
function buildPhraseList(userName?: string): string[] {
  const phrases = ['Fluencia'];
  if (userName?.trim()) phrases.push(userName.trim());
  return phrases;
}

/** Map cancellation error name (from error.message) to a voice-specific i18n key */
/** Check if error.message is a known cancellation error name */
function isKnownCancellationError(msg: string | undefined): msg is 'connection_failure' | 'service_timeout' | 'service_unavailable' | 'service_error' | 'rate_limited' | 'auth_failure' | 'forbidden' {
  switch (msg) {
    case 'connection_failure':
    case 'service_timeout':
    case 'service_unavailable':
    case 'service_error':
    case 'rate_limited':
    case 'auth_failure':
    case 'forbidden':
      return true;
    default:
      return false;
  }
}

interface VoiceRecorderProps {
  /** pronunciationResult is undefined when the recording completed without
   *  real scores (safety-timeout / live-transcription fallback). */
  onStop: (
    transcription: string,
    pronunciationResult: PronunciationResult | undefined
  ) => void;
  onCancel: () => void;
  isRecording: boolean;
  /** When set, uses scripted assessment mode with this as the reference text */
  referenceText?: string;
}

type RecordingState =
  | "idle"
  | "preparing"
  | "recording"
  | "processing"
  | "error";

/**
 * Props for the presentational display component.
 * Exported so Storybook can render it without native dependencies.
 */
export interface VoiceRecorderDisplayProps {
  state: RecordingState;
  seconds: number;
  liveTranscription: string;
  error: string | null;
  silenceWarning: number | null;
  connectionLost?: boolean;
  onCancel: () => void;
  onStop: () => void;
  onRetry: () => void;
}

const formatTime = (s: number) => {
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const BASE_CANCEL_SIZE = 52;
const BASE_ACTION_SIZE = 68;
const BASE_CANCEL_ICON = 20;
const BASE_ACTION_ICON = 24;
const BASE_STATUS_SIZE = 18; // "lg"
const BASE_BODY_SIZE = 16;   // "md"
const BASE_SMALL_SIZE = 14;  // "sm"
const BASE_HINT_SIZE = 12;   // "xs"
const BASE_TRANSCRIPTION_MIN_HEIGHT = 60;
const BASE_DOT_SIZE = 12;

/**
 * Presentational component for the voice recorder overlay.
 * Accepts all visual state as props — no native module imports.
 */
export function VoiceRecorderDisplay({
  state,
  seconds,
  liveTranscription,
  error,
  silenceWarning,
  connectionLost,
  onCancel,
  onStop,
  onRetry,
}: VoiceRecorderDisplayProps) {
  const { fontScale } = useDisplayTheme();
  const { t } = useTranslation();
  // eslint-disable-next-line react-hooks/refs -- Animated.Value is a stable mutable object, reading .current during render is the standard RN pattern
  const pulseAnim = useRef(new Animated.Value(1)).current;
  // eslint-disable-next-line react-hooks/refs -- same as above
  const silenceAnim = useRef(new Animated.Value(1)).current;

  const cancelSize = Math.round(BASE_CANCEL_SIZE * fontScale);
  const actionSize = Math.round(BASE_ACTION_SIZE * fontScale);
  const dotSize = Math.round(BASE_DOT_SIZE * fontScale);
  const transcriptionMinH = Math.round(BASE_TRANSCRIPTION_MIN_HEIGHT * fontScale);

  // Pulse animation for recording indicator.
  // Skipped under E2E_MODE: a continuous native-driver `Animated.loop` never
  // lets Core Animation idle, so XCUITest's accessibility snapshot (WDA's
  // `GET /source` + `POST /element`) can't settle on the recording overlay —
  // the runner then can't see voice-button-stop/cancel mid-recording. Pinning
  // the value makes the recording state quiescent + queryable. The static
  // error state never had this problem. See the 2026-07-05 runner-fix work.
  useEffect(() => {
    if (state === "recording" && !E2E_MODE) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.3,
            duration: 600,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 600,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      pulseAnim.stopAnimation();
      pulseAnim.setValue(1);
    }
  }, [state, pulseAnim]);

  // Faster pulse animation for silence warning. Same E2E_MODE gate as the
  // recording pulse above — keep the overlay quiescent for WDA snapshots.
  useEffect(() => {
    if (silenceWarning !== null && !E2E_MODE) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(silenceAnim, {
            toValue: 1.2,
            duration: 200,
            useNativeDriver: true,
          }),
          Animated.timing(silenceAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      silenceAnim.stopAnimation();
      silenceAnim.setValue(1);
    }
  }, [silenceWarning, silenceAnim]);

  if (state === "idle") return null;

  // Error state
  if (state === "error") {
    return (
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        style={{
          backgroundColor: colors.overlayDark,
          paddingTop: 32,
          paddingHorizontal: 32,
          paddingBottom: Platform.OS === "ios" ? 40 : 32,
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
        }}
      >
        <VStack space="lg" alignItems="center">
          <Ionicons name="warning" size={24 * fontScale} color={colors.error} />
          <Text allowFontScaling={false} style={{ fontSize: BASE_BODY_SIZE * fontScale, color: colors.textDark, textAlign: "center" }}>
            {error || t('chat.voice.recordingFailedError')}
          </Text>

          <HStack space="lg" mt="$4">
            <TouchableOpacity testID="voice-button-cancel-error" accessibilityLabel={t('chat.voice.cancelButton')} accessibilityRole="button" onPress={onCancel} activeOpacity={0.7}>
              <Box
                style={{
                  width: cancelSize,
                  height: cancelSize,
                  backgroundColor: colors.borderDark,
                  borderRadius: cancelSize / 2,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Ionicons name="close" size={BASE_CANCEL_ICON * fontScale} color={colors.border} />
              </Box>
            </TouchableOpacity>

            <TouchableOpacity testID="voice-button-retry" accessibilityLabel={t('chat.voice.retryButton')} accessibilityRole="button" onPress={onRetry} activeOpacity={0.7}>
              <Box
                style={{
                  width: actionSize,
                  height: actionSize,
                  backgroundColor: colors.primary,
                  borderRadius: actionSize / 2,
                  justifyContent: "center",
                  alignItems: "center",
                  shadowColor: colors.success,
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.4,
                  shadowRadius: 20,
                  elevation: 8,
                }}
              >
                <Ionicons name="refresh" size={BASE_ACTION_ICON * fontScale} color="white" />
              </Box>
            </TouchableOpacity>
          </HStack>
        </VStack>
      </Box>
    );
  }

  // Preparing state
  if (state === "preparing") {
    return (
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        style={{
          backgroundColor: colors.overlayDark,
          paddingTop: 32,
          paddingHorizontal: 32,
          paddingBottom: Platform.OS === "ios" ? 40 : 32,
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
        }}
      >
        <VStack space="lg" alignItems="center">
          <Text allowFontScaling={false} style={{ fontSize: BASE_STATUS_SIZE * fontScale, color: colors.textDark }}>
            {t('chat.voice.preparingMicrophone')}
          </Text>
          <Text allowFontScaling={false} style={{ fontSize: BASE_SMALL_SIZE * fontScale, color: colors.textSecondaryDark }}>
            {t('chat.voice.microphonePermission')}
          </Text>

          {/* Cancel — a stalled token fetch must not hold the chat hostage */}
          <TouchableOpacity
            testID="voice-button-cancel-preparing"
            accessibilityLabel={t('chat.voice.cancelButton')}
            accessibilityRole="button"
            onPress={onCancel}
            activeOpacity={0.7}
          >
            <Box
              style={{
                width: cancelSize,
                height: cancelSize,
                backgroundColor: colors.borderDark,
                borderRadius: cancelSize / 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Ionicons name="close" size={BASE_CANCEL_ICON * fontScale} color={colors.border} />
            </Box>
          </TouchableOpacity>
        </VStack>
      </Box>
    );
  }

  // Recording / Processing state
  return (
    <Box
      testID="voice-recorder-overlay"
      position="absolute"
      bottom={0}
      left={0}
      right={0}
      style={{
        backgroundColor: colors.overlayDark,
        paddingTop: 32,
        paddingHorizontal: 32,
        paddingBottom: Platform.OS === "ios" ? 40 : 32,
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
      }}
    >
      <VStack space="lg" alignItems="center">
        {/* Recording indicator */}
        <HStack space="sm" alignItems="center">
          <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
            <Box
              style={{
                width: dotSize,
                height: dotSize,
                backgroundColor: colors.error,
                borderRadius: dotSize / 2,
              }}
            />
          </Animated.View>
          <Text allowFontScaling={false} style={{ fontSize: BASE_STATUS_SIZE * fontScale, color: colors.textDark, fontWeight: "bold" }}>
            {state === "processing" ? t('chat.voice.processing') : t('chat.voice.recording')}
          </Text>
          <Text
            allowFontScaling={false}
            style={{
              fontSize: BASE_STATUS_SIZE * fontScale,
              color: colors.textSecondaryDark,
              fontFamily: Platform.OS === "ios" ? "Courier New" : "monospace",
            }}
          >
            {formatTime(seconds)}
          </Text>
        </HStack>

        {/* Waveform visualization */}
        <Box h={64} w="$full" justifyContent="center" alignItems="center">
          <HStack space="xs">
            {Array.from({ length: 20 }).map((_, i) => {
              const heights = [
                12, 20, 32, 16, 36, 24, 12, 28, 40, 20,
                32, 16, 24, 36, 12, 28, 20, 32, 16, 24,
              ];
              const heightIndex = (i + seconds) % heights.length;
              const heightValue = heights[heightIndex];

              return (
                <Box
                  key={i}
                  w={3}
                  h={heightValue}
                  style={{
                    backgroundColor: colors.primary,
                    borderRadius: 1.5,
                  }}
                />
              );
            })}
          </HStack>
        </Box>

        {/* Live transcription preview */}
        <Box
          testID="voice-transcription"
          w="$full"
          style={{
            minHeight: transcriptionMinH,
            backgroundColor: "rgba(255,255,255,0.06)",
            borderRadius: 16,
            paddingHorizontal: 20,
            paddingVertical: 14,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text
            allowFontScaling={false}
            accessibilityLiveRegion="polite"
            style={{
              fontSize: BASE_BODY_SIZE * fontScale,
              color: liveTranscription ? colors.border : colors.textSecondary,
              textAlign: "center",
              fontStyle: liveTranscription ? "normal" : "italic",
            }}
          >
            {liveTranscription || t('chat.voice.listeningPlaceholder')}
          </Text>
        </Box>

        {/* Silence warning - shown when user pauses */}
        {silenceWarning !== null && (
          <Animated.View style={{ transform: [{ scale: silenceAnim }] }}>
            <Box
              style={{
                backgroundColor: colors.warningBgDark,
                borderRadius: 16,
                paddingHorizontal: 20,
                paddingVertical: 12,
                borderWidth: 1,
                borderColor: colors.warning,
              }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: BASE_SMALL_SIZE * fontScale,
                  color: colors.warning,
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                {t('chat.voice.silenceWarning')}
              </Text>
            </Box>
          </Animated.View>
        )}

        {/* Connection lost warning — recording continues locally but send will be queued */}
        {connectionLost && silenceWarning === null && (state === 'recording' || state === 'processing') && (
          <Box
            style={{
              backgroundColor: colors.warningBgDark,
              borderRadius: 16,
              paddingHorizontal: 20,
              paddingVertical: 12,
              borderWidth: 1,
              borderColor: colors.warning,
            }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: BASE_SMALL_SIZE * fontScale,
                color: colors.warning,
                fontWeight: "600",
                textAlign: "center",
              }}
            >
              {t('chat.voice.connectionLostWarning')}
            </Text>
          </Box>
        )}

        {/* Time limit warning */}
        {seconds >= 25 && silenceWarning === null && (
          <Text allowFontScaling={false} style={{ fontSize: BASE_HINT_SIZE * fontScale, color: colors.warning }}>
            {t('chat.voice.recordingWillStop')}
          </Text>
        )}

        {/* Control buttons */}
        <HStack space="lg" mt="$2">
          {/* Cancel button */}
          <TouchableOpacity
            testID="voice-button-cancel"
            accessibilityLabel={t('chat.voice.cancelButton')}
            accessibilityRole="button"
            onPress={onCancel}
            activeOpacity={0.7}
          >
            <Box
              style={{
                width: cancelSize,
                height: cancelSize,
                backgroundColor: colors.borderDark,
                borderRadius: cancelSize / 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Ionicons name="close" size={BASE_CANCEL_ICON * fontScale} color={colors.border} />
            </Box>
          </TouchableOpacity>

          {/* Stop/Send button */}
          <TouchableOpacity
            testID="voice-button-stop"
            accessibilityLabel={t('chat.voice.stopButton')}
            accessibilityRole="button"
            accessibilityState={{ disabled: state === "processing" }}
            onPress={onStop}
            activeOpacity={0.7}
            disabled={state === "processing"}
          >
            <Box
              style={{
                width: actionSize,
                height: actionSize,
                backgroundColor:
                  state === "processing" ? colors.borderDark : colors.success,
                borderRadius: actionSize / 2,
                justifyContent: "center",
                alignItems: "center",
                opacity: state === "processing" ? 0.7 : 1,
                shadowColor: colors.success,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: state === "processing" ? 0 : 0.4,
                shadowRadius: 20,
                elevation: state === "processing" ? 0 : 8,
              }}
            >
              <Ionicons name={state === "processing" ? "hourglass" : "checkmark"} size={BASE_ACTION_ICON * fontScale} color="white" />
            </Box>
          </TouchableOpacity>
        </HStack>

        <Text allowFontScaling={false} style={{ fontSize: BASE_HINT_SIZE * fontScale, color: colors.textSecondary, textAlign: "center" }}>
          {state === "processing"
            ? t('chat.voice.analyzingHint')
            : t('chat.voice.analyzeHint')}
        </Text>
      </VStack>
    </Box>
  );
}

export function VoiceRecorder({
  onStop,
  onCancel,
  isRecording,
  referenceText,
}: VoiceRecorderProps) {
  const { t } = useTranslation();
  const userName = useUserPreferencesStore(selectDisplayName);
  const targetSttLocale = useUserPreferencesStore(selectTargetSttLocale);
  const pronunciationFeatures = useUserPreferencesStore(selectTargetPronunciationFeatures);

  /** Map native reject code / cancellation error name to a user-facing message */
  const sttErrorMessage = (cancellationError: string | undefined, code?: string): string => {
    // The recognizer rejects with no_match_* when the user never spoke
    // (e.g. initial-silence timeout) — that's "no speech", not a failure.
    // The Expo bridge drops the reject code, so native also prefixes the
    // message with it — match either.
    if (code?.startsWith('no_match') || cancellationError?.startsWith('no_match')) {
      return t('chat.voice.noSpeechDetectedError');
    }
    // The recognizer authenticates to the relay with the live Supabase session
    // (server-speech-proxy Phase 2) — there is no Azure token to invalidate on
    // an auth failure; the next attempt re-reads the current session token.
    if (isKnownCancellationError(cancellationError)) {
      switch (cancellationError) {
        case 'connection_failure':
        case 'auth_failure':
        case 'forbidden':
          return t('chat.voice.connectionError');
        case 'service_timeout':
        case 'service_unavailable':
        case 'service_error':
        case 'rate_limited':
          return t('chat.voice.serviceError');
      }
    }
    return t('chat.voice.recordingFailedError');
  };

  const [state, setState] = useState<RecordingState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [liveTranscription, setLiveTranscription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [silenceWarning, setSilenceWarning] = useState<number | null>(null);

  const subscriptionRef = useRef<EventSubscription | null>(null);
  const hasCompletedRef = useRef(false);
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveTranscriptionRef = useRef("");
  const secondsRef = useRef(0);
  const sessionIdRef = useRef(0);
  const didStartRecordingRef = useRef(false);
  const startingRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Timer. Disabled under E2E_MODE: the 1 Hz setState churns the accessibility
  // tree every second, which (alongside the pulse animation, gated above) keeps
  // WDA's snapshot from settling on the recording overlay. Injected E2E results
  // complete the recording well before the 30s cap this drives, so it's safe to
  // pause in tests.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (state === "recording" && !E2E_MODE) {
      interval = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          secondsRef.current = next;
          if (next >= 30) {
            handleStop();
            return s;
          }
          return next;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleStop uses only refs; adding it would restart the interval every render
  }, [state]);

  // Start recording when isRecording becomes true
  useEffect(() => {
    if (isRecording && state === "idle") {
      startRecording();
    } else if (!isRecording && state === "recording") {
      handleStop();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleStop/startRecording use refs only; state is intentionally excluded to avoid loops
  }, [isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Invalidate the in-flight session so the pending native promise can't
      // setState on an unmounted component or fire completion handlers.
      sessionIdRef.current += 1;
      hasCompletedRef.current = true;
      startingRef.current = false;
      if (subscriptionRef.current) {
        subscriptionRef.current.remove();
      }
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
      }
      ConversationSpeech.cancelRecording();
      // If recording was active, notify parent so machine transitions out of recording.recording
      if (didStartRecordingRef.current) {
        onCancelRef.current();
      }
    };
  }, []);

  const startRecording = async () => {
    // Double-tap guard: a second concurrent start would collide with the
    // active native session (already_recognizing) and flip the UI to error.
    if (startingRef.current) {
      log.debug("Start ignored — session already starting");
      return;
    }
    startingRef.current = true;
    sessionIdRef.current += 1;
    const thisSession = sessionIdRef.current;
    didStartRecordingRef.current = true;
    try {
      setState("preparing");
      setLiveTranscription("");
      liveTranscriptionRef.current = "";
      setSeconds(0);
      secondsRef.current = 0;
      setError(null);
      hasCompletedRef.current = false;

      // Clear any stale stop timeout from a previous recording
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }

      log.debug("Fetching speech relay connection");
      const connection = await getSpeechRelayConnection();
      log.debug("Got speech relay connection", { host: connection.host });

      // User cancelled (or the component unmounted) while the token fetch was
      // in flight — do NOT start a native session nobody can see or stop.
      if (sessionIdRef.current !== thisSession) {
        log.debug("Session invalidated during token fetch — aborting start");
        return;
      }

      subscriptionRef.current = ConversationSpeech.subscribeToEvents({
        onRecognizing: (text) => {
          log.debug("Hearing", { text });
          setLiveTranscription(text);
          liveTranscriptionRef.current = text;
        },
        onCompleted: (result: ConversationResult) => {
          log.info("Recording complete", { transcription: result.transcription });
          setSilenceWarning(null);
          handleRecordingComplete(result);
        },
        onError: (errorMsg, code) => {
          log.warn("Recognition error", { errorMsg, code });
          setSilenceWarning(null);

          // If user pressed stop (safety timeout active) and we have live transcription,
          // use it as a fallback instead of showing an error.
          // This handles degraded audio where stopContinuousRecognition() returns empty
          // text despite having partial recognizing text.
          if (stopTimeoutRef.current && liveTranscriptionRef.current.trim()) {
            log.info("Using live transcription as fallback (no pronunciation scores)");
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
            handleRecordingComplete({
              transcription: liveTranscriptionRef.current,
              durationMs: secondsRef.current * 1000,
              pronunciationResult: undefined,
            });
            return;
          }

          if (stopTimeoutRef.current) {
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
          }
          setState("error");
          setError(sttErrorMessage(errorMsg, code));
        },
        onCanceled: (reason) => {
          log.debug("Recording canceled", { reason });
          setSilenceWarning(null);
          if (stopTimeoutRef.current) {
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
          }
          didStartRecordingRef.current = false;
          setState("idle");
          onCancel();
        },
        onSilenceWarning: (secondsUntilEnd) => {
          log.debug("Silence warning", { secondsUntilEnd });
          setSilenceWarning(secondsUntilEnd);
        },
        onSilenceCleared: () => {
          log.debug("Silence cleared - user speaking again");
          setSilenceWarning(null);
        },
      });

      setState("recording");
      log.info("Starting conversation recording");

      // Start recording — completion is handled by the onCompleted event callback.
      // The promise also resolves with the result, but we only use it for error handling
      // to avoid double-sends (Path A: event callback, Path B: promise resolution).
      await ConversationSpeech.startConversationRecording({
        host: connection.host,
        token: connection.token,
        language: targetSttLocale,
        enablePronunciationAssessment: true,
        maxDurationMs: 30000,
        phraseList: buildPhraseList(userName),
        referenceText,
        pronunciationFeatures,
        // Bill this take's speech to the chat it was spoken into (ADR-026).
        // NB: the chat session — unrelated to `sessionIdRef`, which is this
        // component's recording-take counter.
        sessionId: useConversationStore.getState().currentSessionId ?? undefined,
        shouldAbort: () => sessionIdRef.current !== thisSession,
      }).catch((err: unknown) => {
        // Ignore stale catch from a previous cancelled/replaced session
        if (sessionIdRef.current !== thisSession) return;
        // Only handle errors here if not already completed via event callback
        if (!hasCompletedRef.current) {
          if (stopTimeoutRef.current) {
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
          }
          const code = nativeErrorCode(err);
          const message = err instanceof Error ? err.message : undefined;
          if (code?.startsWith('no_match') || message?.startsWith('no_match')) {
            // Expected user behavior (never spoke) — not an app failure.
            log.info("Recording ended without recognized speech", { code, message });
          } else {
            log.error("Recording failed", err);
          }
          setState("error");
          setError(sttErrorMessage(message, code));
        }
      });

    } catch (err: unknown) {
      log.error("Failed to start recording", err);
      setState("error");
      setError(t('chat.voice.connectionError'));
    } finally {
      // The awaited native promise has settled — safe to start a new session.
      startingRef.current = false;
    }
  };

  const handleRecordingComplete = (result: ConversationResult) => {
    if (hasCompletedRef.current) {
      if (result.pronunciationResult) {
        log.info("Late native result arrived after timeout — pronunciation scores dropped", {
          accuracy: result.pronunciationResult.accuracyScore,
          pronunciation: result.pronunciationResult.pronunciationScore,
        });
      } else {
        log.debug("Ignoring duplicate completion");
      }
      return;
    }
    hasCompletedRef.current = true;
    // Release the start guard here too: a force-completion (10s safety
    // timeout) abandons a native promise that may never settle, and its
    // finally would then never free the guard.
    startingRef.current = false;

    // Clear safety timeout if active
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }

    // Clean up event subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.remove();
      subscriptionRef.current = null;
    }

    // User stopped recording without speaking — dismiss cleanly, no error
    if (result.userCanceled) {
      log.debug("User canceled recording (no speech)");
      didStartRecordingRef.current = false;
      setState("idle");
      onCancelRef.current();
      return;
    }

    // If no speech was recognized, cancel instead of sending empty text
    if (!result.transcription || result.transcription.trim().length === 0) {
      log.info("No speech recognized, showing error");
      setState("error");
      setError(t('chat.voice.noSpeechDetectedError'));
      setLiveTranscription("");
      liveTranscriptionRef.current = "";
      return;
    }

    setState("processing");

    // Map the native assessment to the wire shape. Crucially, carry `profile`
    // and per-word `phonemes` (+ nbestPhonemes / syllables / prosody) through:
    // the backend's scenario-aware formatter keys on `profile` to score against
    // the TARGET word (word/phrase profile). Previously this re-mapped to a
    // subset that dropped both, so the backend always saw `profile` undefined and
    // fell back to `formatFreeProfile` — feeding the agent only the recognized
    // STT text and letting homophones mislead its judgment.
    //
    // `profile` isn't on the native result type; it's derived from the reference
    // text exactly as ConversationSpeech does (selectProfile), so scripted turns
    // report the profile they were assessed under.
    //
    // No native scores (timeout/fallback completion) → undefined so the message
    // goes out as plain text — an all-zero assessment would read as "terrible
    // pronunciation" to the backend/AI, not as "no data".
    const src = result.pronunciationResult;
    const pronunciationResult: PronunciationResult | undefined = src
      ? {
          accuracyScore: src.accuracyScore,
          pronunciationScore: src.pronunciationScore,
          fluencyScore: src.fluencyScore,
          completenessScore: src.completenessScore,
          prosodyScore: src.prosodyScore,
          profile: referenceText?.trim() ? selectProfile(referenceText) : "free",
          words: src.words.map((w) => ({
            word: w.word,
            accuracyScore: w.accuracyScore,
            errorType: w.errorType,
            phonemes: w.phonemes?.map((p) => ({
              phoneme: p.phoneme,
              accuracyScore: p.accuracyScore,
              nbestPhonemes: p.nbestPhonemes?.map((n) => ({ phoneme: n.phoneme, score: n.score })),
            })),
            syllables: w.syllables?.map((s) => ({ syllable: s.syllable, accuracyScore: s.accuracyScore })),
            prosodyFeedback: w.prosodyFeedback ? { ...w.prosodyFeedback } : undefined,
          })),
          rawJson: src.rawJson,
        }
      : undefined;

    didStartRecordingRef.current = false;
    onStop(result.transcription, pronunciationResult);

    setState("idle");
    setSeconds(0);
    setLiveTranscription("");
    liveTranscriptionRef.current = "";
  };

  const handleStop = () => {
    if (hasCompletedRef.current) {
      log.debug("Already completed, ignoring stop");
      return;
    }

    log.debug("Stopping recording");
    setState("processing");

    // Stop native recording. The continuous recognizer stops and merges
    // segments (scripted takes are a single segment — if the user spoke their
    // scores are preserved, otherwise it resolves as userCanceled).
    // Do NOT call onStop() directly here — let the event-driven path handle it to prevent double-sends.
    ConversationSpeech.stopRecording();

    // Safety timeout: if the native onCompleted event doesn't fire within 10 seconds
    // (e.g., native module unresponsive, slow connection to Azure), force-complete
    // with whatever live transcription is available.
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }
    stopTimeoutRef.current = setTimeout(() => {
      if (!hasCompletedRef.current) {
        log.warn("Voice recording stop timed out (10s): forcing completion with live transcription");
        const currentTranscription = liveTranscriptionRef.current;
        handleRecordingComplete({
          transcription: currentTranscription,
          durationMs: secondsRef.current * 1000,
          pronunciationResult: undefined,
        });
      }
    }, 10000);
  };

  const handleCancel = () => {
    log.debug("Canceling recording");
    // Invalidate the in-flight session: the pending native promise must not
    // flip state to error afterwards, and the stop safety timeout must not
    // force-send a message the user just cancelled.
    sessionIdRef.current += 1;
    hasCompletedRef.current = true;
    // Release the start guard — the abandoned session's finally may never run
    // (stalled token fetch), and a blocked guard would silently swallow the
    // next mic tap while the store still thinks recording is active.
    startingRef.current = false;
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    didStartRecordingRef.current = false;
    ConversationSpeech.cancelRecording();

    if (subscriptionRef.current) {
      subscriptionRef.current.remove();
      subscriptionRef.current = null;
    }

    setState("idle");
    setSeconds(0);
    setLiveTranscription("");
    liveTranscriptionRef.current = "";
    onCancel();
  };

  const handleRetry = () => {
    setError(null);
    startRecording();
  };

  const socketConnected = useSocketStatus((s) => s.status === 'connected');

  if (!isRecording && state === "idle") return null;

  return (
    <VoiceRecorderDisplay
      state={state}
      seconds={seconds}
      liveTranscription={liveTranscription}
      error={error}
      silenceWarning={silenceWarning}
      connectionLost={!socketConnected}
      onCancel={handleCancel}
      onStop={handleStop}
      onRetry={handleRetry}
    />
  );
}
